import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Clock from '../components/Clock';
import Numpad from '../components/Numpad';
import Header from '../components/Header';
import PinpadModal from '../components/PinpadModal';
import InOutBoard from '../components/InOutBoard';
import { useTimeClock } from '../hooks/useTimeClock';
import { Employee, EmployeeStatus, GroupInterface, DisplayGroup, EditableTimeSegment, NameDisplayFormat, TimeEntrySource, EmployeeRole, Activity, TimeLog, PinpadSize } from '../../shared/types.js';
import Button from '../components/Button';
import { LOCAL_STORAGE_KEYS } from '../../shared/constants';
import { correctDateForTimezone } from '../../shared/utils.js';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import InsertLunchModal from '../components/InsertLunchModal';
import EntryEditorModal from '../components/EntryEditorModal';
import ManualTimeEntryModal from '../components/ManualTimeEntryModal';
import { convertTimeLogToEditableSegment, formatEmployeeName, getTodaysShiftForEmployee, cloneDeep } from '../utils.js';
import Screensaver from '../components/Screensaver';
import { PlusIcon, MinusIcon, GridIcon, ListIcon } from '../components/icons';

type ComputedStatus = EmployeeStatus | 'ABSENT' | 'ON_LEAVE' | 'AUTO_OUT';

const EmployeeCard: React.FC<{ employee: Employee; computedStatus: ComputedStatus; onClick: () => void; onContextMenu: (e: React.MouseEvent) => void; nameFormat?: NameDisplayFormat, sizeIndex: number }> = ({ employee, computedStatus, onClick, onContextMenu, nameFormat, sizeIndex }) => {
    const statusInfo: Record<ComputedStatus, { text: string; color: string }> = {
        [EmployeeStatus.CLOCKED_IN]: { text: 'Clocked In', color: 'bg-green-500' },
        [EmployeeStatus.ON_BREAK]: { text: 'On Lunch', color: 'bg-yellow-500' },
        [EmployeeStatus.CLOCKED_OUT]: { text: 'Clocked Out', color: 'bg-gray-500' },
        'AUTO_OUT': { text: 'Auto Clock Out', color: 'bg-gray-500' },
        'ABSENT': { text: 'Absent', color: 'bg-red-500' },
        'ON_LEAVE': { text: 'On Leave', color: 'bg-purple-500' },
    };
	
	const ringByStatus: Record<ComputedStatus, string> = {
      [EmployeeStatus.CLOCKED_IN]:  'ring-2 ring-green-500',
      [EmployeeStatus.ON_BREAK]:    'ring-2 ring-yellow-500',
      [EmployeeStatus.CLOCKED_OUT]: 'dark:focus:ring-2 dark:focus:ring-brand-secondary',
      'AUTO_OUT': 'dark:focus:ring-2 dark:focus:ring-brand-secondary',
      ABSENT:                       'ring-2 ring-red-500',
      ON_LEAVE:                     'ring-2 ring-purple-500',
    };
  
    const displayName = formatEmployeeName(employee, nameFormat);

    const cardSizeConfig: Record<number, { p: string; name: string; status: string }> = {
      0: { p: 'p-4', name: 'text-xl', status: 'text-sm' },
      1: { p: 'p-6', name: 'text-2xl', status: 'text-base' },
      2: { p: 'p-8', name: 'text-3xl', status: 'text-lg' },
	  3: { p: 'p-10', name: 'text-4xl', status: 'text-xl' },
	  4: { p: 'p-12', name: 'text-5xl', status: 'text-2xl' },
    };
    const sizeClasses = cardSizeConfig[sizeIndex] || cardSizeConfig[1];

    return (
        <button
            onClick={onClick}
            onContextMenu={onContextMenu}
			className={`bg-white dark:bg-gray-800 rounded-lg ${sizeClasses.p} text-center shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow-xl transition-all duration-200 ease-in-out transform hover:-translate-y-1 focus:outline-none ${ringByStatus[computedStatus] || 'dark:focus:ring-2 dark:focus:ring-brand-secondary'}`}
			aria-label={`Select ${displayName}`}
        >
            <h3 className={`${sizeClasses.name} font-bold text-gray-900 dark:text-white mb-2 truncate`}>{displayName}</h3>
            <div className="flex items-center justify-center gap-2" aria-hidden="true">
                <span className={`h-3 w-3 rounded-full ${statusInfo[computedStatus].color}`}></span>
                <p className={`${sizeClasses.status} text-gray-500 dark:text-gray-400`}>{statusInfo[computedStatus].text}</p>
            </div>
        </button>
    );
};

const KioskScreen: React.FC = () => {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const { employees, displayGroups, timeLogs, activities, leaveEntries, leaveCategories, insertLunchBreak, updateTimeLog, addTimeLog, deleteTimeLogs, updateEmployee, shifts, handleClockAction, getGroupForEmployee, getActivityId, schedules, payrollSettings, screensaverSettings, updateDisplayGroup, pinpadSize, groupContentSizes, updateGroupContentSize } = useTimeClock();
    const navigate = useNavigate();
    
    const [now, setNow] = useState(Date.now());
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
    
    const [isScreensaverActive, setIsScreensaverActive] = useState(false);
    const inactivityTimerRef = useRef<number | undefined>(undefined);
	
	const [isManualTimeModalOpen, setIsManualTimeModalOpen] = useState(false);
    const [manualAction, setManualAction] = useState<'clock-in' | 'clock-out' | null>(null);
    const [selectedEmployeeForManualEntry, setSelectedEmployeeForManualEntry] = useState<Employee | null>(null);
    
    useEffect(() => {
        const resetInactivityTimer = () => {
            if (typeof inactivityTimerRef.current === 'number') {
                window.clearTimeout(inactivityTimerRef.current);
            }
            if (screensaverSettings?.enabled) {
                inactivityTimerRef.current = window.setTimeout(() => {
                    setIsScreensaverActive(true);
                }, screensaverSettings.timeoutMinutes * 60 * 1000);
            }
        };

        const events: (keyof WindowEventMap)[] = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
        const handleActivity = () => {
            if (isScreensaverActive) {
                setIsScreensaverActive(false);
            }
            resetInactivityTimer();
        };

        if (screensaverSettings?.enabled) {
            events.forEach(event => window.addEventListener(event, handleActivity));
            resetInactivityTimer();
        }

        return () => {
            events.forEach(event => window.removeEventListener(event, handleActivity));
            if (typeof inactivityTimerRef.current === 'number') {
                window.clearTimeout(inactivityTimerRef.current);
            }
        };
    }, [screensaverSettings, isScreensaverActive]);
	
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; employee: Employee } | null>(null);
    const [headerContextMenu, setHeaderContextMenu] = useState<{ x: number; y: number } | null>(null);
    const [isInsertLunchModalOpen, setIsInsertLunchModalOpen] = useState(false);
    const [segmentToSplit, setSegmentToSplit] = useState<EditableTimeSegment | null>(null);
    const [isEditEntryModalOpen, setIsEditEntryModalOpen] = useState(false);
    const [editingSegment, setEditingSegment] = useState<EditableTimeSegment | null>(null);
    const lockTimestamp = useMemo(() => {
        if (!payrollSettings?.startDate) return 0;
        const lockDate = correctDateForTimezone(payrollSettings.startDate);
        lockDate.setHours(0, 0, 0, 0);
        return lockDate.getTime();
    }, [payrollSettings?.startDate]);
    const isTimestampLocked = useCallback((timestamp?: number | null) => {
        return typeof timestamp === 'number' && timestamp < lockTimestamp;
    }, [lockTimestamp]);
    const isSegmentLocked = useCallback((segment: EditableTimeSegment) => {
        return isTimestampLocked(segment.startTime);
    }, [isTimestampLocked]);

    useEffect(() => {
        const checkAuth = () => {
            setIsAdminAuthenticated(localStorage.getItem('isAdminAuthenticated') === 'true');
        };
        checkAuth();
        
        const handleStorageChange = (storageEvent: StorageEvent) => {
            if (storageEvent.key === 'isAdminAuthenticated') {
                checkAuth();
            }
        };
        window.addEventListener('storage', handleStorageChange);

        const closeContextMenus = () => {
            setContextMenu(null);
            setHeaderContextMenu(null);
        };
        window.addEventListener('click', closeContextMenus);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('click', closeContextMenus);
        };
    }, []);
    
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
        return () => clearInterval(timer);
    }, []);

    const sortedDisplayGroups = useMemo(() => {
        return [...displayGroups].sort((a, b) => a.name.localeCompare(b.name));
    }, [displayGroups]);

    useEffect(() => {
        if (sortedDisplayGroups.length > 0) {
            const storedGroupId = localStorage.getItem(LOCAL_STORAGE_KEYS.SELECTED_GROUP_ID);
            const isValidStoredId = storedGroupId && sortedDisplayGroups.some(g => g.id === storedGroupId);
            
            if (isValidStoredId) {
                setSelectedGroupId(storedGroupId);
            } else {
                setSelectedGroupId(sortedDisplayGroups[0].id);
            }
        }
    }, [sortedDisplayGroups]);

    useEffect(() => {
        if (selectedGroupId) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.SELECTED_GROUP_ID, selectedGroupId);
        }
    }, [selectedGroupId]);

    useEffect(() => {
        setPin('');
        setError('');
        setIsProcessing(false);
        setSelectedEmployee(null); 
    }, [selectedGroupId]);

    const selectedGroup = useMemo<DisplayGroup | undefined>(() => {
        return displayGroups.find(g => g.id === selectedGroupId);
    }, [displayGroups, selectedGroupId]);

    const employeesInGroup = useMemo<Employee[]>(() => {
        if (!selectedGroup) return [];
        const groupEmployeeIds = new Set(selectedGroup.employeeIds);
        const nameFormat = selectedGroup.settings.nameFormat || NameDisplayFormat.FIRST_LAST;

        return employees
            .filter(e => groupEmployeeIds.has(e.id) && e.role !== EmployeeRole.INACTIVE)
            .sort((a, b) => {
                const aName = nameFormat === NameDisplayFormat.LAST_FIRST
                    ? `${a.lastName || ''}, ${a.firstName || ''}`
                    : `${a.firstName || ''} ${a.lastName || ''}`;
                const bName = nameFormat === NameDisplayFormat.LAST_FIRST
                    ? `${b.lastName || ''}, ${b.firstName || ''}`
                    : `${b.firstName || ''} ${b.lastName || ''}`;
                return aName.localeCompare(bName);
            });
    }, [employees, selectedGroup]);


    const handleEmployeeSelect = (employee: Employee) => {
        const requirePIN = selectedGroup?.settings?.options?.requireEmployeePINs ?? true;
        if (selectedGroup &&
            (selectedGroup.settings.groupInterface === GroupInterface.GRID || selectedGroup.settings.groupInterface === GroupInterface.IN_OUT_BOARD) &&
            !requirePIN) {
            navigate('/dashboard', { state: { employee } });
        } else {
            setSelectedEmployee(employee);
            setPin('');
            setError('');
            setIsProcessing(false);
        }
    };

    const handleCloseModal = useCallback(() => {
        setSelectedEmployee(null);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('isAdminAuthenticated');
        setIsAdminAuthenticated(false);
    };

    const handleKeyPress = useCallback((key: string) => {
        if (isProcessing) return;
        setError('');
        setPin(prevPin => prevPin.length < 8 ? prevPin + key : prevPin);
    }, [isProcessing]);

    const handleClear = useCallback(() => {
        if (isProcessing) return;
        setPin('');
        setError('');
    }, [isProcessing]);

    const handleDelete = useCallback(() => {
        if (isProcessing) return;
        setPin(prevPin => prevPin.slice(0, -1));
    }, [isProcessing]);

    const handleContextMenu = (event: React.MouseEvent, employee: Employee) => {
        if (!isAdminAuthenticated) return;
        event.preventDefault();
        setHeaderContextMenu(null);
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            employee,
        });
    };

    const handleHeaderContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        setContextMenu(null);
        setHeaderContextMenu({
            x: event.clientX,
            y: event.clientY,
        });
    };

    const handleEditEntries = () => {
        if (!contextMenu) return;
        const { employee } = contextMenu;

        const employeeLogs = timeLogs
            .filter(log => log.employeeId === employee.emp_number)
            .sort((a, b) => b.startTime - a.startTime);
        
        const mostRecentLog = employeeLogs[0];

        if (mostRecentLog) {
            if (isTimestampLocked(mostRecentLog.startTime)) {
                alert("Cannot edit time entries from a closed period.");
                return;
            }
            const activity = activities.find(a => a.id === mostRecentLog.activityId);
            if (activity) {
                const segment = convertTimeLogToEditableSegment(mostRecentLog, employee, activity);
                setEditingSegment(segment);
                setIsEditEntryModalOpen(true);
            } else {
                 alert(`Could not find activity for time entry.`);
            }
        } else {
            alert(`${employee.firstName} has no time entries to edit.`);
        }
    };

    const handleInsertLunch = () => {
        if (!contextMenu) return;
        const employeeLogs = timeLogs
            .filter(log => log.employeeId === contextMenu.employee.emp_number)
            .sort((a, b) => b.startTime - a.startTime);
        
        const inProgressInLog = employeeLogs.find(log => !log.endTime && log.activityId === getActivityId('In'));
        
        if (inProgressInLog) {
            if (isTimestampLocked(inProgressInLog.startTime)) {
                alert("Cannot insert lunch breaks into a closed period.");
                return;
            }
            const activity = activities.find(a => a.id === inProgressInLog.activityId);
            if(activity) {
                const segment = convertTimeLogToEditableSegment(inProgressInLog, contextMenu.employee, activity);
                setSegmentToSplit(segment);
                setIsInsertLunchModalOpen(true);
            }
        } else {
            alert(`${contextMenu.employee.firstName} is not currently clocked in.`);
        }
    };

    const handleDetailReport = () => {
        if (contextMenu) {
            navigate('/admin', { state: { initialTab: 'detailReport', filterEmployeeId: contextMenu.employee.id } });
        }
    };

    const handleLogin = useCallback(async () => {
        setIsProcessing(true);
        setError('');

        const employeeToAuth = selectedEmployee;
        if (!employeeToAuth) {
            setIsProcessing(false);
            return;
        }

        const employeeGroups = displayGroups.filter(g => g.employeeIds.includes(employeeToAuth.id));
        const allManagerIds = new Set(employeeGroups.flatMap(g => g.managerIds));
        const manager = employees.find(e => allManagerIds.has(e.id) && e.pin === pin);

        if (employeeToAuth.pin === pin || manager) {
            const isManagerAccessingOther = !!manager && manager.id !== employeeToAuth.id;
            navigate('/dashboard', { state: { employee: employeeToAuth, isAdmin: isManagerAccessingOther } });
        } else {
            setError('Invalid PIN');
            setPin('');
        }
        setIsProcessing(false);
    }, [selectedEmployee, pin, displayGroups, employees, navigate]);
    
    useEffect(() => {
        const isPinEntryActive = !!selectedEmployee || selectedGroup?.settings.groupInterface === GroupInterface.PIN_ONLY;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isPinEntryActive) return;

            if (selectedEmployee) {
                if (event.key === 'Enter' && !isProcessing) {
                    event.preventDefault();
                    handleLogin();
                } else if (event.key === 'Escape') {
                    event.preventDefault();
                    handleCloseModal();
                }
            }

            if (event.key >= '0' && event.key <= '9') {
                event.preventDefault();
                handleKeyPress(event.key);
            } else if (event.key === 'Backspace') {
                event.preventDefault();
                handleDelete();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedEmployee, selectedGroup, isProcessing, handleLogin, handleKeyPress, handleDelete, handleCloseModal]);
    
    useEffect(() => {
        if (selectedGroup?.settings.groupInterface === GroupInterface.PIN_ONLY && pin.length >= 4) {
            const employee = employees.find(e => e.pin === pin);
            if(employee && employeesInGroup.some(e => e.id === employee.id)) {
                navigate('/dashboard', { state: { employee } });
            } else if (pin.length > 3) {
                setError('Invalid PIN');
            }
        }
    }, [pin, selectedGroup, employees, employeesInGroup, navigate]);

    useEffect(() => {
        const handleAdminHotkey = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'a') {
                event.preventDefault();
                navigate('/admin-login');
            }
        };

        window.addEventListener('keydown', handleAdminHotkey);

        return () => {
            window.removeEventListener('keydown', handleAdminHotkey);
        };
    }, [navigate]);

    const handleSizeChange = (delta: number) => {
        if (!selectedGroupId || !selectedGroup) return;
        const currentSize = groupContentSizes[selectedGroupId] ?? 1;
        const newSize = Math.max(0, Math.min(4, currentSize + delta));
        if (newSize !== currentSize) updateGroupContentSize(selectedGroupId, newSize);
    };
    
    const handleInterfaceToggle = () => {
        if (!selectedGroup) return;
        const currentInterface = selectedGroup.settings.groupInterface;
        const newInterface = currentInterface === GroupInterface.GRID ? GroupInterface.IN_OUT_BOARD : GroupInterface.GRID;
        const newGroup = cloneDeep(selectedGroup);
        if (!newGroup.settings) return;
        newGroup.settings.groupInterface = newInterface;
        updateDisplayGroup(newGroup);
    };
    
    const pinpadContainerWidthClass = {
        [PinpadSize.HIDDEN]: 'w-[365px]',
        [PinpadSize.SMALL]: 'w-[365px]',
        [PinpadSize.MEDIUM]: 'w-[465px]',
        [PinpadSize.LARGE]: 'w-[665px]',
    }[pinpadSize];

    const renderKioskContent = () => {
        if (!payrollSettings) return null;

        const interfaceType = selectedGroup?.settings.groupInterface || GroupInterface.GRID;
        const nameFormat = selectedGroup?.settings.nameFormat;
        const contentSize = selectedGroup ? (groupContentSizes[selectedGroup.id] ?? 1) : 1;

        const gridColConfig: Record<number, string> = {
            0: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6',
            1: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
            2: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
			3: 'grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3',
			4: 'grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-2',
        };
        const gridCols = gridColConfig[contentSize] || gridColConfig[1];

        switch (interfaceType) {
            case GroupInterface.IN_OUT_BOARD:
                return <InOutBoard 
                    employees={employeesInGroup} 
                    onSelectEmployee={handleEmployeeSelect} 
                    displayOptions={selectedGroup.settings.inOutBoardOptions} 
                    nameFormat={nameFormat} 
                    onContextMenu={handleContextMenu}
                    schedules={schedules}
                    shifts={shifts}
                    payrollSettings={payrollSettings}
                    displayGroups={displayGroups}
                    sizeIndex={contentSize}
                />;
            case GroupInterface.PIN_ONLY:
                 return (
                    <div className="flex flex-col items-center">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Enter Your PIN</h2>
                        <input
                            type="password"
                            readOnly
                            value={pin.replace(/./g, '•')}
                            className="w-64 text-center text-4xl p-2 rounded-lg bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white mb-4"
                            aria-label="PIN input"
                        />
                        {error && <p className="text-red-500 text-lg mb-4">{error}</p>}
                        {pinpadSize !== PinpadSize.HIDDEN &&
                            <Numpad onKeyPress={handleKeyPress} onClear={handleClear} onDelete={handleDelete} size={pinpadSize} />
                        }
                    </div>
                );
            case GroupInterface.GRID:
            default:
                return (
                     <div className={`grid ${gridCols} gap-6 p-4`}>
                        {employeesInGroup.map(employee => {
                            let computedStatus: ComputedStatus = employee.status;

                            const employeeLogs = timeLogs
                                .filter(log => log.employeeId === employee.emp_number)
                                .sort((a, b) => b.startTime - a.startTime);
                            const lastLog = employeeLogs[0];

                            const today = new Date(now);
                            today.setHours(0, 0, 0, 0);
                            const todayTimestamp = today.getTime();
                            const dayOfWeek = today.getDay();

                            const isOnLeaveToday = leaveEntries.some(le => {
                                if (!le.employeeIds.includes(employee.id)) return false;
                                if (todayTimestamp < le.startDate || todayTimestamp > le.endDate) return false;
                                const isOrangeHRMLeave = le.id.startsWith('ohrm_');
                                const skipSaturday = le.skipSaturday ?? true;
                                const skipSunday = le.skipSunday ?? true;
                                if (!isOrangeHRMLeave && ((skipSaturday && dayOfWeek === 6) || (skipSunday && dayOfWeek === 0))) {
                                    return false;
                                }
                                
                                return true;
                            });

                            if (isOnLeaveToday) {
                                computedStatus = 'ON_LEAVE';
                            } else if (employee.status === EmployeeStatus.CLOCKED_OUT) {
                                const todaysShift = getTodaysShiftForEmployee(employee.id, schedules, shifts, payrollSettings, displayGroups, new Date(now), false);
                                if (todaysShift) {
                                    const nowTime = new Date(now);
                                    const [startHours, startMinutes] = todaysShift.startTime.split(':').map(Number);
                                    const shiftStartTime = new Date(nowTime.getFullYear(), nowTime.getMonth(), nowTime.getDate(), startHours, startMinutes).getTime();

                                    if (now > shiftStartTime) {
                                        const inActivityId = getActivityId('In');
                                        const hasClockedInForShift = timeLogs.some(log => 
                                            log.employeeId === employee.emp_number && 
                                            log.startTime >= shiftStartTime &&
                                            log.activityId === inActivityId
                                        );

                                        if (!hasClockedInForShift) {
                                            computedStatus = 'ABSENT';
                                        } else if (lastLog?.source === TimeEntrySource.AUTO) {
                                            computedStatus = 'AUTO_OUT';
                                        }
                                    } else if (lastLog?.source === TimeEntrySource.AUTO) {
                                        computedStatus = 'AUTO_OUT';
                                    }
                                } else if (lastLog?.source === TimeEntrySource.AUTO) {
                                    computedStatus = 'AUTO_OUT';
                                }
                            }

                            return (
                                <EmployeeCard key={employee.id} employee={employee} computedStatus={computedStatus} onClick={() => handleEmployeeSelect(employee)} nameFormat={nameFormat} onContextMenu={(e) => handleContextMenu(e, employee)} sizeIndex={contentSize} />
                            )
                        })}
                    </div>
                );
        }
    };
    
    const isManualClockInDisabled = useMemo(() => {
        if (!contextMenu) return true;
        return contextMenu.employee.status !== EmployeeStatus.CLOCKED_OUT;
    }, [contextMenu]);

    const isManualClockOutDisabled = useMemo(() => {
        if (!contextMenu) return true;
        return contextMenu.employee.status === EmployeeStatus.CLOCKED_OUT;
    }, [contextMenu]);

    const isEditEntriesDisabled = useMemo(() => {
        if (!contextMenu) return true;
        const mostRecentLog = timeLogs
            .filter(log => log.employeeId === contextMenu.employee.emp_number)
            .sort((a, b) => b.startTime - a.startTime)[0];
        return mostRecentLog ? isTimestampLocked(mostRecentLog.startTime) : false;
    }, [contextMenu, timeLogs, isTimestampLocked]);

    const isInsertLunchDisabled = useMemo(() => {
        if (!contextMenu) return true;
        const inProgressInLog = timeLogs
            .filter(log => log.employeeId === contextMenu.employee.emp_number)
            .find(log => !log.endTime && log.activityId === getActivityId('In'));
        return inProgressInLog ? isTimestampLocked(inProgressInLog.startTime) : false;
    }, [contextMenu, timeLogs, getActivityId, isTimestampLocked]);

    const handleManualClockInClick = () => {
        if (!contextMenu || contextMenu.employee.status !== EmployeeStatus.CLOCKED_OUT) return;
        setSelectedEmployeeForManualEntry(contextMenu.employee);
        setManualAction('clock-in');
        setIsManualTimeModalOpen(true);
        setContextMenu(null);
    };

    const handleManualClockOutClick = () => {
        if (!contextMenu || contextMenu.employee.status === EmployeeStatus.CLOCKED_OUT) return;
        setSelectedEmployeeForManualEntry(contextMenu.employee);
        setManualAction('clock-out');
        setIsManualTimeModalOpen(true);
        setContextMenu(null);
    };

    const handleManualTimeSave = async (timestamp: number) => {
        if (!selectedEmployeeForManualEntry || !manualAction) return;
        if (isTimestampLocked(timestamp)) {
            alert(`Cannot ${manualAction === 'clock-in' ? 'clock in' : 'clock out'} for a closed period.`);
            return;
        }
        try {
            await handleClockAction(selectedEmployeeForManualEntry.id, timestamp, TimeEntrySource.ADMIN);
            setIsManualTimeModalOpen(false);
            setSelectedEmployeeForManualEntry(null);
            setManualAction(null);
        } catch (error: any) {
            alert(error?.message || 'Manual time entry failed.');
        }
    };
	
    return (
        <>
            {isScreensaverActive && screensaverSettings && <Screensaver settings={screensaverSettings} />}
            <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white flex flex-col items-center justify-center p-4 select-none">
                <Header isAdminAuthenticated={isAdminAuthenticated} onLogout={handleLogout} onContextMenu={handleHeaderContextMenu} />
                
				<div
					className={`w-full flex-grow flex flex-col items-center ${
						isAdminAuthenticated ? "justify-start pt-20 pb-32" : "justify-center sm:pt-0 pt-20"
					}`}
				>
					{!isAdminAuthenticated && (
						<div className="mb-8 hidden sm:block">
							<Clock />
						</div>
					)}
					
					<div className="w-full">
						{renderKioskContent()}
					</div>
				</div>

                <PinpadModal isOpen={!!selectedEmployee} onClose={handleCloseModal} title={`Enter PIN for ${selectedEmployee?.firstName || ''}`}>
                     <div className="flex flex-col items-center">
                        <div className={pinpadContainerWidthClass}>
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 text-center">{`${selectedEmployee?.firstName} ${selectedEmployee?.lastName}`.trim()}</h2>
                            <input
                                type="password"
                                readOnly
                                value={pin.replace(/./g, '•')}
                                className="w-full text-center text-5xl p-4 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white mb-6"
                                aria-label="PIN input"
                            />
                            {error && <p className="text-red-500 text-lg mb-4 text-center">{error}</p>}
                            {pinpadSize !== PinpadSize.HIDDEN &&
                                <Numpad onKeyPress={handleKeyPress} onClear={handleClear} onDelete={handleDelete} size={pinpadSize} />
                            }
                            <Button onClick={handleLogin} disabled={isProcessing} size="lg" className="mt-6 w-full text-2xl">
                                {isProcessing ? 'Verifying...' : 'Login'}
                            </Button>
                        </div>
                    </div>
                </PinpadModal>
                
                {contextMenu && (
                    <ContextMenu x={contextMenu.x} y={contextMenu.y} onClose={() => setContextMenu(null)}>
                        <ContextMenuItem onClick={handleManualClockInClick} disabled={isManualClockInDisabled}>Manual Clock In</ContextMenuItem>
						<ContextMenuItem onClick={handleManualClockOutClick} disabled={isManualClockOutDisabled}>Manual Clock Out</ContextMenuItem>
						<ContextMenuItem onClick={handleEditEntries} disabled={isEditEntriesDisabled}>Edit Time Entries</ContextMenuItem>
                        <ContextMenuItem onClick={handleInsertLunch} disabled={isInsertLunchDisabled}>Insert Lunch Break</ContextMenuItem>
                        <ContextMenuItem onClick={handleDetailReport}>Run Detail Report</ContextMenuItem>
                    </ContextMenu>
                )}

                {headerContextMenu && (
                    <ContextMenu x={headerContextMenu.x} y={headerContextMenu.y} onClose={() => setHeaderContextMenu(null)}>
                        <ContextMenuItem onClick={() => { navigate('/admin-login'); setHeaderContextMenu(null); }}>
                            Admin Login
                        </ContextMenuItem>
                    </ContextMenu>
                )}

                {editingSegment && isEditEntryModalOpen && (
                    <EntryEditorModal
                        isOpen={isEditEntryModalOpen}
                        onClose={() => setIsEditEntryModalOpen(false)}
                        onSave={async (data) => {
                            const { activityId, startTime, endTime } = data;
                            if (!editingSegment?.timeLogId) return;
                            if (isTimestampLocked(startTime)) {
                                alert("Cannot edit time entries into a closed period.");
                                return;
                            }
                            
                            await updateTimeLog(editingSegment.timeLogId, { activityId, startTime, endTime });

                            setIsEditEntryModalOpen(false);
                        }}
                        employees={employees}
                        initialData={editingSegment}
                        activities={activities}
                        isLocked={isSegmentLocked(editingSegment)}
                        lockTimestamp={lockTimestamp}
                    />
                )}
               
                {segmentToSplit && isInsertLunchModalOpen && (
                     <InsertLunchModal
                        isOpen={isInsertLunchModalOpen}
                        onClose={() => setIsInsertLunchModalOpen(false)}
                        onSave={(data) => {
                            if (isTimestampLocked(data.begin) || isTimestampLocked(data.end)) {
                                alert("Cannot insert lunch breaks into a closed period.");
                                return;
                            }
                            if(segmentToSplit.timeLogId) {
                               insertLunchBreak(segmentToSplit.timeLogId, data.begin, data.end);
                            }
                            setIsInsertLunchModalOpen(false);
                        }}
                        segment={segmentToSplit}
                    />
                )}
                 <ManualTimeEntryModal
                    isOpen={isManualTimeModalOpen}
                    onClose={() => setIsManualTimeModalOpen(false)}
                    onSave={handleManualTimeSave}
                    title={manualAction === 'clock-in' ? 'Manual Clock In' : 'Manual Clock Out'}
                />
            </div>

            {isAdminAuthenticated && selectedGroup && (
                <footer className="fixed bottom-0 left-0 right-0 bg-gray-200/80 dark:bg-gray-800/80 backdrop-blur-sm p-2 z-20">
                    <div className="max-w-7xl mx-auto flex flex-wrap md:flex-nowrap justify-center items-center gap-3 md:gap-6 select-none overflow-x-auto px-2">
                        
                        {/* Group Selector */}
                        <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">Group:</span>
                            <select
                                value={selectedGroupId || ''}
                                onChange={(e) => setSelectedGroupId(e.target.value)}
                                className="bg-white/50 dark:bg-gray-900/50 border border-gray-300 dark:border-gray-600 rounded-md py-1 px-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-secondary"
                                aria-label="Select display group"
                            >
                                {sortedDisplayGroups.map(group => (
                                    <option key={group.id} value={group.id}>
                                        {group.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Display Type */}
                        <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">Display Type:</span>
                            <Button variant="ghost" className="p-2 rounded-full" onClick={handleInterfaceToggle} title="Toggle View">
                                {selectedGroup.settings.groupInterface === GroupInterface.GRID ? <ListIcon /> : <GridIcon />}
                            </Button>
                        </div>

                        {/* Display Size */}
                        <div className="flex items-center gap-2 whitespace-nowrap">
							<span className="text-sm font-semibold text-gray-600 dark:text-gray-300">Display Size:</span>
                            <Button variant="ghost" className="p-2 rounded-full" onClick={() => handleSizeChange(-1)} disabled={!selectedGroupId || (groupContentSizes[selectedGroupId] ?? 1) <= 0}>
                                <MinusIcon />
                            </Button>
                            <span className="text-sm w-12 text-center text-gray-600 dark:text-gray-300">
                                Size
                            </span>
                             <Button variant="ghost" className="p-2 rounded-full" onClick={() => handleSizeChange(1)} disabled={!selectedGroupId || (groupContentSizes[selectedGroupId] ?? 1) >= 4}>
                                <PlusIcon />
                            </Button>
                        </div>
                    </div>
                </footer>
            )}
        </>
    );
};

export default KioskScreen;
